import { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import { resolveDisplayCampaignName } from './resolveDisplayCampaignName';

// Custom Campaign-cell dropdown for the master grid (Pre-P2b-3 polish
// Fix 4). Native <select> couldn't honour the "trigger shows resolved
// name (override or campaign.name); options list shows raw
// campaign.name only" UX rule — a <select>'s trigger text is its
// selected <option>'s text. So this component is a button trigger +
// click-outside-dismissible popover modelled on RowActionsMenu.
//
// Behaviour:
//   - Trigger shows resolveDisplayCampaignName(row, campaign) plus a
//     small ✎ chip when an override is set.
//   - Tooltip on the trigger when override is set: "Custom name
//     override — actual Campaign: <Campaign.name>".
//   - Opening reveals an option list. Each option shows
//     campaign.name + " (Alt)" / " (Opt Out)" suffix as the existing
//     <select> did. The override doesn't appear in the option labels.
//   - Confirm dialog on Campaign reassignment (existing behaviour
//     preserved by the caller's onChange handler).
//   - Disabled when archived.
//
// Visible options come from the parent — same visibility + status
// filter applies as before. Caller passes the pre-filtered list and
// only renders this when there's at least one option to show.

type Props = {
  row: AccountCampaign;
  // Currently-assigned Campaign object (when the row has a campaignId).
  // Used for the trigger label fallback.
  currentCampaign: Campaign | null;
  // Pre-filtered visible Campaigns. Caller is responsible for
  // including the row's currently-assigned Campaign even if
  // visibility/status would otherwise exclude it (existing
  // master-grid behaviour).
  options: Campaign[];
  disabled: boolean;
  // Confirmation gate is owned by the caller — it knows whether
  // changing campaignId triggers a content-snapshot replacement.
  onSelect: (campaignId: string | null) => void;
};

export function CampaignCellDropdown({
  row,
  currentCampaign,
  options,
  disabled,
  onSelect,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  // Escape closes the popover. Mirrors other popover surfaces in this
  // module so the keyboard contract is consistent.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const triggerLabel = resolveDisplayCampaignName(row, currentCampaign);
  const triggerTitle =
    row.campaignNameOverride && currentCampaign
      ? `Custom name override — actual Campaign: ${currentCampaign.name}`
      : currentCampaign
        ? currentCampaign.name
        : 'Pick a Campaign';

  return (
    <div ref={rootRef} className="relative inline-flex items-center gap-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={triggerTitle}
        className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs max-w-[180px] truncate text-left hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-slate-900">{triggerLabel}</span>
        <span className="text-slate-400 ml-1" aria-hidden>
          ▾
        </span>
      </button>
      {row.campaignNameOverride && (
        <span
          className="text-xs italic text-amber-700"
          title={`Custom name: "${row.campaignNameOverride}"`}
          aria-label="Custom name override"
        >
          ✎
        </span>
      )}

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg py-1 text-sm max-h-72 overflow-y-auto"
        >
          <button
            type="button"
            role="option"
            aria-selected={row.campaignId === null}
            onClick={() => {
              setOpen(false);
              onSelect(null);
            }}
            className={`block w-full px-3 py-1 text-left hover:bg-slate-50 ${
              row.campaignId === null ? 'bg-slate-100' : ''
            }`}
          >
            <span className="text-slate-500 italic">— No Campaign —</span>
          </button>
          {options.map((c) => {
            const isCurrent = c.id === row.campaignId;
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => {
                  setOpen(false);
                  onSelect(c.id);
                }}
                className={`block w-full px-3 py-1 text-left hover:bg-slate-50 ${
                  isCurrent ? 'bg-slate-100' : ''
                }`}
              >
                <span className="text-slate-900">{c.name}</span>
                {c.isAlternate && (
                  <span className="ml-1 text-xs text-indigo-700">(Alt)</span>
                )}
                {c.isOptOut && (
                  <span className="ml-1 text-xs text-rose-700">(Opt Out)</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
